import React from "react";
import dayjs from "dayjs";
import { Trans } from "@lingui/macro";
import { useSelector } from "react-redux";
import { Alert } from "../../../../../UI/Alert/Alert";
import { Button } from "../../../../../UI/Button/Button";
import { numberCurrencyFormat } from "../../../../../../constants/convertToLocalCurrency";
import { CheckProductItem } from "../../../dataSales/popupContents/popupPrint/CheckProductItem";
import { selectRefundsArchiveDetailItem } from "../../../../../../model/dashboardRefundsArchive/selectors";

export type typePopupPrint = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PopupPrint: React.FC<typePopupPrint> = ({ setOpenPopup }) => {
  const dataObject = useSelector(selectRefundsArchiveDetailItem);

  let vat: number = 0;

  if (dataObject) {
    if (dataObject!.productList.some((item) => item.discountAmount !== 0)) {
      vat = dataObject.productList.reduce((acc, curr) => {
        const discountPercent =
          curr.discountAmount > 0
            ? curr.discountAmount /
              (curr.customTotal ? curr.customTotal / curr.amount : curr.price)
            : 0;
        const currVat =
          curr.tax !== null
            ? curr.tax
            : curr.product?.vat
              ? curr.product.vat
              : 0;
        const currPrice =
          (curr.customTotal ? curr.customTotal / curr.amount : curr.price) *
          (1 - discountPercent);
        const vatForItem = (currVat * currPrice) / (1 + currVat);
        return acc + vatForItem * curr.amount;
      }, 0);
    } else {
      vat = dataObject.productList.reduce((acc, curr) => {
        const currVat =
          curr.tax !== null
            ? curr.tax
            : curr.product?.vat
              ? curr.product.vat
              : 0;

        // noinspection PointlessArithmeticExpressionJS
        const currPrice =
          (curr.customTotal ? curr.customTotal / curr.amount : curr.price) * 1;

        const vatForItem = (currVat * currPrice) / (1 + currVat);

        return acc + vatForItem * curr.amount;
      }, 0);
    }
  }

  return dataObject ? (
    <div className="w-80">
      <h1 className="text-gray-90 text-center font-bold">
        <Trans>Tinda</Trans>
      </h1>

      <div className="border-b text-sm pb-2">
        <div className=" flex flex-wrap gap-x-4 py-1 justify-between">
          <div className="text-gray-500 text-sm text-left leading-4">
            <Trans>Date:</Trans>
          </div>

          <div className="text-gray-90 text-sm text-left leading-4">
            {dataObject?.date
              ? dayjs(dataObject.date).format("YYYY-MM-DD")
              : "-"}
          </div>
        </div>

        <div className=" flex flex-wrap gap-x-4 py-1 justify-between">
          <div className="text-gray-500 text-sm text-left leading-4">
            <Trans>Time:</Trans>
          </div>

          <div className="text-gray-90 text-sm text-left leading-4">
            {dataObject?.date ? dayjs(dataObject.date).format("HH:mm:ss") : "-"}
          </div>
        </div>

        {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
          <div className="flex flex-wrap gap-x-4 py-1 justify-between">
            <div className="text-gray-500  text-sm text-left leading-4">
              <Trans>Fiscal module ID:</Trans>
            </div>

            <div className="text-gray-90 text-sm text-left leading-4">
              {dataObject?.fmId || "-"}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-x-4 py-1 justify-between">
          <div className="text-gray-500 text-sm text-left leading-4">
            <Trans>Applet version:</Trans>
          </div>

          <div className="text-gray-90 text-sm text-left leading-4">
            {dataObject?.appletVersion || "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-1 justify-between">
          <div className="text-gray-500 text-sm text-left leading-4">
            <Trans>Receipt number:</Trans>
          </div>

          <div className="text-gray-90 text-sm text-left leading-4">
            {dataObject?.receiptNumber || "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-1 justify-between">
          <div className="flex gap-x-2 items-center text-gray-500 text-sm text-left leading-4">
            <Trans> --- </Trans>
          </div>
          <div className="text-gray-90 text-sm text-left leading-4">-</div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-1 justify-between">
          <div className="flex gap-x-2 items-center text-gray-500 text-sm text-left leading-4">
            <Trans>User name: </Trans>
          </div>

          <div className="text-gray-90 text-sm text-left uppercase leading-4">
            {dataObject?.userName || "-"}
          </div>
        </div>
      </div>

      <h2 className="text-gray-90 text-center font-medium">
        <Trans>Refunds</Trans>
      </h2>

      <h3 className="text-gray-500 text-center font-normal text-md mb-2">
        <Trans>Copy</Trans>
      </h3>

      <div className="mt-1 text-sm">
        {dataObject.productList.length ? (
          dataObject.productList.map((item, index) => {
            if (item.markedLabels && item.markedLabels.length) {
              const vatForItem =
                (((item.customTotal
                  ? item.customTotal / item.amount
                  : item.price) -
                  (item.discountAmount || 0)) *
                  (item.tax ? item.tax : item?.product?.vat || 0)) /
                (1 + (item.tax ? item.tax : item?.product?.vat || 0));

              // @ts-ignore
              return item.markedLabels.map((mark: string, subIndex: number) => {
                return (
                  <CheckProductItem
                    key={subIndex + index}
                    name={item.productName || item?.product?.name || ""}
                    amount={1}
                    price={item.price}
                    customTotal={
                      item.customTotal
                        ? item.customTotal / item.amount
                        : item.price
                    }
                    units={item?.product?.units}
                    vat={item.tax ? item.tax : item?.product?.vat || 0}
                    vatForItem={vatForItem}
                    psid={item.psid ? item.psid : item?.product?.psid}
                    marked={mark}
                    barcodes={item.barcodes}
                    commissionTIN={item.commissionTIN || undefined}
                    commissionPINFL={item.commissionPINFL || undefined}
                  />
                );
              });
            } else {
              const vatForItem =
                (((item.customTotal
                  ? item.customTotal / item.amount
                  : item.price) -
                  (item.discountAmount || 0)) *
                  (item.tax ? item.tax : item?.product?.vat || 0)) /
                (1 + (item.tax ? item.tax : item?.product?.vat || 0));

              return (
                <CheckProductItem
                  key={index}
                  price={item.price}
                  amount={item.amount}
                  vatForItem={vatForItem}
                  barcodes={item.barcodes}
                  units={item?.product?.units}
                  customTotal={item.customTotal}
                  commissionTIN={item.commissionTIN || undefined}
                  vat={item.tax ? item.tax : item?.product?.vat || 0}
                  commissionPINFL={item.commissionPINFL || undefined}
                  name={item.productName || item?.product?.name || ""}
                  psid={item.psid ? item.psid : item?.product?.psid || ""}
                />
              );
            }
          })
        ) : (
          <div>
            <Trans>The product list is empty</Trans>
          </div>
        )}
      </div>

      <div className="border-b pb-1 text-sm">
        <div className="grid gap-x-8 grid-cols-1 px-2 pb-2 mb-1">
          {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
            <div className="flex flex-wrap gap-x-4 py-1 justify-between">
              <div className="text-left text-sm text-gray-500 leading-4">
                <Trans>VAT including</Trans>:
              </div>

              <div className="text-sm text-gray-500 text-left leading-4">
                {numberCurrencyFormat(vat)}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-x-4 py-1 justify-between">
            <div className="text-left text-sm text-gray-900 leading-4">
              <Trans>Refund amount</Trans>:
            </div>

            <div className="text-sm text-gray-900text-left leading-4">
              {numberCurrencyFormat(+dataObject.amount)}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end print:hidden">
        <div className="flex gap-4 mt-5 mr-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <Button
            type="button"
            secondary={true}
            label={<Trans>Print</Trans>}
            onClick={() => {
              window.print();
              setOpenPopup(false);
            }}
          />
        </div>

        <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <Button
            type="button"
            secondary={true}
            label={<Trans>Cancel</Trans>}
            onClick={() => setOpenPopup(false)}
          />
        </div>
      </div>
    </div>
  ) : (
    <Alert type="error" title={<Trans>Have no data.</Trans>} />
  );
};
